/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

export type PromptBlock = {
  /** Display id shown in the `[...]` part of the title link. */
  traceId: string;
  /** Original `okahu://...` href, used to deep-link into the trace. */
  href: string;
  /** Multi-line input / system prompt text. */
  input: string;
  /** Raw output text (often JSON). */
  output: string;
};

/** `[label](href)` -> `label`, so plain-text rendering doesn't show markdown. */
function stripMarkdownLinks(text: string): string {
  return text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
}

/** Strip the leading indentation the agent adds to wrapped item lines. */
function dedent(text: string): string {
  return text
    .split('\n')
    .map((line) => line.replace(/^\s{1,4}/, ''))
    .join('\n')
    .trim();
}

/**
 * Parse the body of an `okahu:prompt-list` fence into structured blocks.
 *
 * Each item looks like:
 *   1. **[<traceId>](okahu://.../traces/<id>)**
 *      Input: <possibly multi-line>
 *      Output: <possibly multi-line>
 */
export function parsePromptList(content: string): PromptBlock[] {
  return content
    .trim()
    .split(/\n(?=\d+\.\s)/)
    .map((raw) => raw.trim())
    .filter(Boolean)
    .map((block) => {
      const [titleLine, ...rest] = block.split('\n');
      const linkMatch = titleLine.match(/\[([^\]]+)\]\(([^)]+)\)/);
      const traceId =
        linkMatch?.[1]?.trim() ??
        titleLine
          .replace(/^\d+\.\s*/, '')
          .replace(/\*/g, '')
          .trim();
      const href = linkMatch?.[2]?.trim() ?? '';

      const body = rest.join('\n');
      const inputMatch = body.match(/Input:\s*([\s\S]*?)(?:\n\s*Output:|$)/);
      const outputMatch = body.match(/Output:\s*([\s\S]*)$/);

      return {
        traceId,
        href,
        input: dedent(stripMarkdownLinks(inputMatch?.[1] ?? '')),
        output: dedent(outputMatch?.[1] ?? ''),
      };
    })
    .filter((block) => block.input || block.output);
}

/** Best-effort pretty-print of the agent's quirky JSON; falls back to raw text. */
export function formatMaybeJson(raw: string): string {
  const text = raw.trim();
  if (!text) return '';
  try {
    // Normalize the agent's quirks: doubled quotes ("") and single-quoted keys.
    const cleaned = text.replace(/""/g, '"').replace(/'/g, '"');
    return JSON.stringify(JSON.parse(cleaned), null, 2);
  } catch {
    return text;
  }
}

/** True while a streamed fence has no complete numbered item yet. */
export function isPromptListIncomplete(content: string): boolean {
  const text = content.trim();
  if (!text) return true;
  return text.split(/\n(?=\d+\.\s)/).every((part) => !part.trim());
}
